// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Quote conversion popover web component rendering interface for allowing
 * user to add quote to basket, or save the quote to email.
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { App } from "../../../app/App.js"

// @@imports-utils
import {
    apiRequest,
    cloneTemplate,
    crossFadeElements,
    fadeElementIn,
    fadeElementOut
} from "../../../utils/index.js"

// @@imports-types
import { HTTP_METHODS } from "../../../types/ApiRequest.js"
import { RawLineItem } from "../../../../../package/types/LineItem.js"
import { BlockTabbedContainer } from "../../containers/BlockTabbedContainer.js"

// @@body
class QuoteConversionPopover extends HTMLElement {
    constructor() {
        super()
    }

    connectedCallback() {
        const card = cloneTemplate("quote-conversion")

        const form = this.#role === "sales"
            ? /** @type {HTMLFormElement} */ (card.querySelector("[data-accessor='sales-form']"))
            : /** @type {HTMLFormElement} */ (card.querySelector("[data-accessor='public-form']"))
        const primaryButtons = /** @type {HTMLDivElement} */ (card
            .querySelector("div#add-to-basket"))
        const secondaryButtons = /** @type {HTMLDivElement} */ (card
            .querySelector("div#save-quote"))

        const addToBasket = /** @type {HTMLButtonElement} */ (primaryButtons
            .querySelector("button.primary"))
        const openForm = /** @type {HTMLButtonElement} */ (primaryButtons
            .querySelector("button.secondary"))
        const saveQuote = /** @type {HTMLButtonElement} */ (secondaryButtons
            .querySelector("button.primary"))
        const closeForm = /** @type {HTMLButtonElement} */ (secondaryButtons
            .querySelector("button.secondary"))

        if (this.#lifecycle === "updating") {
            openForm.innerText = "Update Quote"
        }

        addToBasket.addEventListener("click", () => {
            this.saveQuote({ addToBasket: true })
        })
        saveQuote.addEventListener("click", () => { this.saveQuote() })

        openForm.addEventListener("click", () => {
            if (this.#lifecycle === "updating") { this.saveQuote() }
            else {
                fadeElementIn(form)
                crossFadeElements(primaryButtons, secondaryButtons)
            }
        })
        closeForm.addEventListener("click", () => {
            fadeElementOut(form)
            crossFadeElements(secondaryButtons, primaryButtons)
        })

        card.querySelectorAll("form").forEach(form => {
            form.addEventListener("input", () => {
                this.formData ? saveQuote.classList.remove("inactive")
                    : saveQuote.classList.add("inactive")
            })
        });
        /** @type {BlockTabbedContainer} */ (card
            .querySelector("[data-accessor='sales-form']"))
            .addEventListener("tab-selected", () => {
                this.formData ? saveQuote.classList.remove("inactive")
                    : saveQuote.classList.add("inactive")
            })

        // id.innerText = /** @type {EpdmCalculator} */ (this.#quote.calculator).id
        // price.innerText = this.#quote.calculator?.bom.price
        // vatPrice.innerText = this.#quote.calculator?.bom.vatPrice
        if (App.instance.state.pageOptions.hideBasket) {
            openForm.classList.remove("secondary")
            openForm.classList.add("primary")
        }

        this.append(card)

        // this.#addToBasket("abc", "quoteId")
    }

    async saveQuote({ addToBasket = false } = {}) {
        // Unload stepped container, displaying loading screen whilst required
        // API requests etc. are made for saving quote.
        const unload = App.instance.container.unload({ unloadNavs: true })

        // Fetch required line items and line item diffs with only selected keys
        // required for saving (creating or updating) quote in server.
        const lineItems = this.#quote.calculator.requiredBom.rawLineItems
            .map(({ uuid, quantity, retailPrice, detail }) => {
                return { uuid, quantity, retailPrice, detail }
            })
        const lineItemDiffs = this.#quote.calculator.differencesBom.rawLineItems
            .map(({ uuid, quantity, retailPrice }) => {
                return { uuid, quantity, retailPrice }
            })

        // Build request body with all line items and new quote and client
        // details depending on current lifecycle and if client has used add to
        // basket button or not (add to basket skips client details, so form
        // data will be empty).
        const body = /** @type {Object.<string, any>} */ ({
            lineItems,
            lineItemDiffs,
            purchaseStatus: addToBasket ? "basket" : "saved",
            state: this.#quote.calculator.serialize()
        })
        if (this.#lifecycle !== "updating") {
            Object.assign(body, {
                quoteId: this.#quote.calculator.id,
                priceList: App.instance.state.pageOptions.priceList,
                clientType: App.instance.state.pageOptions.clientType,
                hideBasket: App.instance.state.pageOptions.hideBasket,
                hidePrices: App.instance.state.pageOptions.hidePrices
            })

            if (!addToBasket) { Object.assign(body, this.formData) }
        }

        let quote
        if (this.#role === "client") {
            // Build path depending on lifecycle.
            let path = `quotes/${this.#quote.merchant.uuid}`
            if (this.#lifecycle === "updating") {
                path += `/${this.#quote.uuid}`
            }

            // Save (create/update) quote.
            quote = await apiRequest(path, {
                method: this.#lifecycle === "updating"
                    ? HTTP_METHODS.patch
                    : HTTP_METHODS.post,
                body,
                token: App.instance.state.pageOptions.token
            })
        }
        else {
            const message = {
                action: this.#lifecycle === "updating"
                    ? "UPDATE_QUOTE"
                    : "SAVE_QUOTE",
                body
            }
            window.parent.postMessage(message, "*")
        }

        // If adding to basket, dispatch required window message with *all* line
        // items (required combined with differences) *excluding* the line item
        // details.
        if (addToBasket) {
            const lineItems = this.#quote.calculator.bom.rawLineItems
                .map(({ uuid, sku, quantity, price }) => {
                    return { uuid, sku, quantity, price }
                })
            const message = {
                action: "ADD_TO_BASKET",
                uuid: quote?.uuid,
                quoteId: quote?.quoteId,
                lineItems
            }
            window.parent.postMessage(message, "*")
            App.instance.state.pageOptions.lifecycle = "basket"
        }

        // Terminate calculator app (renders final "thank you" modal) *after*
        // save has been fully executed.
        await unload
        App.instance.container.end()
    }

    /**
     * Get client details object in format expected by the server for saving new
     * quotes.
     */
    get formData() {
        // Get client details form data from quote submission popover.
        let form
        if (this.#role === "sales") {
            const { activeTab } = /** @type {BlockTabbedContainer} */ (this
                .querySelector("[data-accessor='sales-form']"))
            form = /** @type {HTMLFormElement} */ (this
                .querySelector(`div.tab[data-tab='${activeTab}'] form`))
        }
        else {
            form = /** @type {HTMLFormElement} */ (this
                .querySelector("[data-accessor='public-form']"))
        }
        const formData = new FormData(form)

        // Construct client details object expected by server saving quotes.
        const data = /** @type {Object.<string, string>} */ ({})
        for (const [key, value] of formData.entries()) {
            // Fetch string value of form input and infer if value is a
            // correctly formatted email or phone string or any other string
            // containing only safe characters.
            let string = value.toString()
            const isEmail = string.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
            const isPhone = string.match(/^(0|\+\d{2})\d{10}$/)
            const isSafe = string.match(/^[0-9A-Za-z-_]*$/)

            // Sanitize client inputs.
            switch (key) {
                case "email":
                    if (!isEmail) { return null }
                    break
                case "phone":
                    if (!isPhone) { return null }
                    string = string.replace(/^0/, "+44")
                    break
                default: if (!string || !isSafe) { return null }
            }

            data[key] = string
        }

        return data
    }

    get #lineItems() {}

    get #quote() {
        return App.instance.state.quote
    }

    get #role() {
        return App.instance.state.pageOptions.role
    }

    get #lifecycle() {
        return App.instance.state.pageOptions.lifecycle
    }

    static get customName() {
        return "quote-conversion-popover"
    }
}

// @@exports
export { QuoteConversionPopover }
